import {
  ClipboardTextIcon,
  DownloadSimpleIcon,
  FileJsIcon,
  FileMdIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { useDialogFocus } from '../hooks';
import { useI18n } from '../i18n';

export type ExportAction = 'copy-json' | 'download-json' | 'copy-markdown' | 'download-markdown';

interface ExportDialogProps {
  readonly defaultProjectName: string;
  readonly onExport: (action: ExportAction, includeProject: boolean, projectName: string) => void;
  readonly onClose: () => void;
}

export function ExportDialog({ defaultProjectName, onExport, onClose }: ExportDialogProps) {
  const { t } = useI18n();
  const [includeProject, setIncludeProject] = useState(false);
  const [projectName, setProjectName] = useState(defaultProjectName);
  const dialogRef = useDialogFocus(onClose);

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="dialog export-dialog"
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="export-dialog-title"
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="dialog-heading">
          <div>
            <h2 id="export-dialog-title">{t('export.title')}</h2>
            <p>{t('export.description')}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label={t('actions.close')}
            onClick={onClose}
          >
            <XIcon aria-hidden="true" />
          </button>
        </div>

        <label className="checkbox-field">
          <input
            data-dialog-initial-focus
            type="checkbox"
            checked={includeProject}
            onChange={(event) => {
              setIncludeProject(event.currentTarget.checked);
            }}
          />
          <span>{t('export.includeProject')}</span>
        </label>
        <label className="dialog-field compact-field">
          <span>{t('export.projectName')}</span>
          <input
            type="text"
            value={projectName}
            disabled={!includeProject}
            onChange={(event) => {
              setProjectName(event.currentTarget.value);
            }}
          />
          <small>{t('export.projectHelp')}</small>
        </label>

        <div className="export-grid">
          <button
            className="export-option"
            type="button"
            onClick={() => {
              onExport('copy-markdown', includeProject, projectName);
            }}
          >
            <ClipboardTextIcon aria-hidden="true" />
            <strong>{t('export.copyMarkdown')}</strong>
            <span>{t('export.copyMarkdownHelp')}</span>
          </button>
          <button
            className="export-option"
            type="button"
            onClick={() => {
              onExport('download-markdown', includeProject, projectName);
            }}
          >
            <FileMdIcon aria-hidden="true" />
            <strong>{t('export.downloadMarkdown')}</strong>
            <span>{t('export.downloadMarkdownHelp')}</span>
          </button>
          <button
            className="export-option"
            type="button"
            onClick={() => {
              onExport('copy-json', includeProject, projectName);
            }}
          >
            <FileJsIcon aria-hidden="true" />
            <strong>{t('export.copyJson')}</strong>
            <span>{t('export.copyJsonHelp')}</span>
          </button>
          <button
            className="export-option"
            type="button"
            onClick={() => {
              onExport('download-json', includeProject, projectName);
            }}
          >
            <DownloadSimpleIcon aria-hidden="true" />
            <strong>{t('export.downloadJson')}</strong>
            <span>{t('export.downloadJsonHelp')}</span>
          </button>
        </div>

        <div className="dialog-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('actions.close')}
          </button>
        </div>
      </section>
    </div>
  );
}
